import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AppointmentsService } from '../../api/AppointmentsAPI';
import { LOCATION_META } from '../../constants';
import { toDateStr } from '../../utils';
import s from '../../styles/analytics.module.css';

const RANGES = [
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: '1w', label: '1W', days: 7 },
  { id: '1m', label: '1M', days: 30 },
  { id: '3m', label: '3M', days: 90 },
  { id: '6m', label: '6M', days: 180 },
  { id: '1y', label: '1Y', days: 365 },
];
const DEFAULT_RANGE = '1m';

// Resolve a range id to { start, end } (YYYY-MM-DD). Month presets cover the
// whole calendar month regardless of whether every day has data.
function rangeToDates(rangeId) {
  const now = new Date();
  if (rangeId === 'thisMonth') {
    return {
      start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)),
      end:   toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (rangeId === 'lastMonth') {
    return {
      start: toDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      end:   toDateStr(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  const days = RANGES.find(r => r.id === rangeId)?.days || 30;
  const endD = new Date();
  const startD = new Date();
  startD.setDate(startD.getDate() - (days - 1));
  return { start: toDateStr(startD), end: toDateStr(endD) };
}

const LOC_SERIES = Object.keys(LOCATION_META).map((id, i) => ({
  id,
  name: LOCATION_META[id].name,
  color: LOCATION_META[id].color,
  shape: ['circle', 'square', 'triangle', 'cross'][i] || 'circle',
}));

// ── formatting helpers ──────────────────────────────────────────────────────
const money = (n) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const moneyAxis = (n) => {
  if (n >= 1000) return `$${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: n >= 10000 ? 0 : 1 })}k`;
  return `$${Math.round(n)}`;
};

const shortDate = (ds) =>
  new Date(ds + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
const longDate = (ds) =>
  new Date(ds + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

function niceCeil(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
}

// A single marker shape, filled in the series colour with a surface-coloured
// ring so overlapping points stay legible.
function Marker({ shape, cx, cy, color, surface, r = 3.4 }) {
  const ring = { stroke: surface, strokeWidth: 1.4 };
  if (shape === 'square') return <rect x={cx - r} y={cy - r} width={2 * r} height={2 * r} fill={color} {...ring} />;
  if (shape === 'triangle')
    return <polygon points={`${cx},${cy - r * 1.2} ${cx - r},${cy + r * 0.9} ${cx + r},${cy + r * 0.9}`} fill={color} {...ring} />;
  if (shape === 'diamond')
    return <polygon points={`${cx},${cy - r * 1.3} ${cx + r * 1.15},${cy} ${cx},${cy + r * 1.3} ${cx - r * 1.15},${cy}`} fill={color} {...ring} />;
  if (shape === 'cross') {
    const w = r * 0.42, e = r * 1.15;
    return (
      <polygon
        points={`${cx - w},${cy - e} ${cx + w},${cy - e} ${cx + w},${cy - w} ${cx + e},${cy - w} ${cx + e},${cy + w} ${cx + w},${cy + w} ${cx + w},${cy + e} ${cx - w},${cy + e} ${cx - w},${cy + w} ${cx - e},${cy + w} ${cx - e},${cy - w} ${cx - w},${cy - w}`}
        fill={color}
        {...ring}
      />
    );
  }
  return <circle cx={cx} cy={cy} r={r} fill={color} {...ring} />;
}

// ── the chart ───────────────────────────────────────────────────────────────
const VIEW_W = 820;
const VIEW_H = 340;
const PAD = { l: 56, r: 20, t: 16, b: 34 };
const PLOT_W = VIEW_W - PAD.l - PAD.r;
const PLOT_H = VIEW_H - PAD.t - PAD.b;

function RevenueChart({ days, series, theme }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  const n = days.length;
  const maxTotal = Math.max(0, ...days.map((d) => d.total));
  const yMax = niceCeil(maxTotal);

  const xFor = (i) => (n <= 1 ? PAD.l + PLOT_W / 2 : PAD.l + (i / (n - 1)) * PLOT_W);
  const yFor = (v) => PAD.t + PLOT_H - (v / yMax) * PLOT_H;

  const valuesFor = (serie) =>
    serie.id === 'total' ? days.map((d) => d.total) : days.map((d) => d.byLocation[serie.id] || 0);

  const linePath = (vals) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');

  const yTicks = [0, 1, 2, 3, 4].map((k) => (yMax * k) / 4);
  const maxLabels = 7;
  const xStep = Math.max(1, Math.ceil(n / maxLabels));
  const xTickIdx = days.map((_, i) => i).filter((i) => i % xStep === 0 || i === n - 1);
  const showMarkers = n > 1 && PLOT_W / (n - 1) >= 18;

  const handleMove = (e) => {
    if (!svgRef.current || n === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const vbX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    let idx = Math.round(((vbX - PAD.l) / PLOT_W) * (n - 1));
    idx = Math.max(0, Math.min(n - 1, idx));
    setHover(idx);
  };

  // Tooltip horizontal placement, flipped away from the edges.
  const hoverX = hover != null ? xFor(hover) : 0;
  const leftPct = (hoverX / VIEW_W) * 100;
  const tipTransform = leftPct > 68 ? 'translateX(-100%)' : leftPct < 20 ? 'translateX(0)' : 'translateX(-50%)';

  return (
    <div className={s.chartWrap}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        className={s.svg}
        role="img"
        aria-label="Daily revenue by location"
      >
        {/* y grid + labels */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={yFor(v)} y2={yFor(v)} stroke={theme.grid} strokeWidth={1} />
            <text x={PAD.l - 8} y={yFor(v) + 3.5} textAnchor="end" fontSize="10" fill={theme.axis}>
              {moneyAxis(v)}
            </text>
          </g>
        ))}

        {/* x labels */}
        {xTickIdx.map((i) => (
          <text key={i} x={xFor(i)} y={VIEW_H - PAD.b + 16} textAnchor="middle" fontSize="10" fill={theme.axis}>
            {shortDate(days[i].date)}
          </text>
        ))}

        {/* hover crosshair */}
        {hover != null && (
          <line x1={xFor(hover)} x2={xFor(hover)} y1={PAD.t} y2={PAD.t + PLOT_H} stroke={theme.grid} strokeWidth={1.5} />
        )}

        {/* series lines */}
        {series.map((serie) => {
          const vals = valuesFor(serie);
          const isTotal = serie.id === 'total';
          return (
            <path
              key={serie.id}
              d={linePath(vals)}
              fill="none"
              stroke={serie.color}
              strokeWidth={isTotal ? 1.6 : 1}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={isTotal ? 1 : 0.95}
            />
          );
        })}

        {/* per-point markers (shape = redundant series identity) */}
        {showMarkers &&
          series.map((serie) => {
            const vals = valuesFor(serie);
            return (
              <g key={`m-${serie.id}`}>
                {vals.map((v, i) => (
                  <Marker key={i} shape={serie.shape} cx={xFor(i)} cy={yFor(v)} color={serie.color} surface={theme.surface} />
                ))}
              </g>
            );
          })}

        {/* highlighted points at hover */}
        {hover != null &&
          series.map((serie) => {
            const vals = valuesFor(serie);
            return (
              <Marker
                key={`h-${serie.id}`}
                shape={serie.shape}
                cx={xFor(hover)}
                cy={yFor(vals[hover])}
                color={serie.color}
                surface={theme.surface}
                r={4.6}
              />
            );
          })}

        {/* pointer capture overlay */}
        <rect
          x={PAD.l}
          y={PAD.t}
          width={PLOT_W}
          height={PLOT_H}
          fill="transparent"
          onPointerMove={handleMove}
          onPointerDown={handleMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>

      {hover != null && (
        <div className={s.tooltip} style={{ left: `${leftPct}%`, transform: tipTransform }}>
          <div className={s.tooltipDate}>{longDate(days[hover].date)}</div>
          {series.map((serie) => {
            const vals = valuesFor(serie);
            return (
              <div key={serie.id} className={`${s.tooltipRow} ${serie.id === 'total' ? s.tooltipTotal : ''}`}>
                <span className={s.tooltipDot} style={{ background: serie.color }} />
                <span className={s.tooltipName}>{serie.name}</span>
                <span className={s.tooltipVal}>{money(vals[hover])}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── legend (colour + shape swatch per series) ───────────────────────────────
function LegendSwatch({ shape, color }) {
  return (
    <svg width="26" height="12" viewBox="0 0 26 12" aria-hidden="true">
      <line x1="1" y1="6" x2="25" y2="6" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
      <g transform="translate(13,6)">
        {shape === 'square' ? (
          <rect x="-3.4" y="-3.4" width="6.8" height="6.8" fill={color} />
        ) : shape === 'triangle' ? (
          <polygon points="0,-4 -3.4,3 3.4,3" fill={color} />
        ) : shape === 'diamond' ? (
          <polygon points="0,-4.4 3.8,0 0,4.4 -3.8,0" fill={color} />
        ) : shape === 'cross' ? (
          <polygon points="-1.4,-3.9 1.4,-3.9 1.4,-1.4 3.9,-1.4 3.9,1.4 1.4,1.4 1.4,3.9 -1.4,3.9 -1.4,1.4 -3.9,1.4 -3.9,-1.4 -1.4,-1.4" fill={color} />
        ) : (
          <circle r="3.4" fill={color} />
        )}
      </g>
    </svg>
  );
}

// ── data table ──────────────────────────────────────────────────────────────
function DataTable({ days, locSeries }) {
  const totals = useMemo(() => {
    const acc = { total: 0 };
    locSeries.forEach((l) => (acc[l.id] = 0));
    days.forEach((d) => {
      locSeries.forEach((l) => (acc[l.id] += d.byLocation[l.id] || 0));
      acc.total += d.total;
    });
    return acc;
  }, [days, locSeries]);

  // Table lists newest day first; the chart keeps chronological order.
  const rows = useMemo(() => [...days].reverse(), [days]);

  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>Date</th>
            {locSeries.map((l) => (
              <th key={l.id} className={s.num}>{l.name}</th>
            ))}
            <th className={s.num}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.date}>
              <td>{longDate(d.date)}</td>
              {locSeries.map((l) => (
                <td key={l.id} className={s.num}>{money(d.byLocation[l.id] || 0)}</td>
              ))}
              <td className={`${s.num} ${s.strong}`}>{money(d.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className={s.strong}>Range total</td>
            {locSeries.map((l) => (
              <td key={l.id} className={`${s.num} ${s.strong}`}>{money(totals[l.id])}</td>
            ))}
            <td className={`${s.num} ${s.strong}`}>{money(totals.total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── panel ───────────────────────────────────────────────────────────────────
export function AnalyticsPanel({ light }) {
  const today = toDateStr(new Date());
  const [rangeId, setRangeId] = useState(DEFAULT_RANGE);
  const [custom, setCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { start, end } = useMemo(() => {
    // A complete custom range wins; otherwise fall back to the active preset.
    if (custom && customStart && customEnd) {
      return customStart <= customEnd
        ? { start: customStart, end: customEnd }
        : { start: customEnd, end: customStart };
    }
    return rangeToDates(rangeId);
  }, [custom, customStart, customEnd, rangeId]);

  // The server caps ranges at 366 days; guard here for a friendlier message.
  const spanDays = (new Date(end + 'T00:00:00Z') - new Date(start + 'T00:00:00Z')) / 86400000;
  const tooLong = spanDays > 366;

  const selectPreset = (id) => {
    setCustom(false);
    setRangeId(id);
  };

  const enableCustom = () => {
    if (!custom) {
      setCustomStart(start);
      setCustomEnd(end);
    }
    setCustom(true);
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['analytics', start, end],
    queryFn: () => AppointmentsService.getAnalytics(start, end),
    placeholderData: (prev) => prev,
    enabled: !tooLong,
  });

  const days = data?.days || [];
  const locSeries = LOC_SERIES;
  const series = useMemo(
    () => [...locSeries, { id: 'total', name: 'Total', color: light ? 'var(--text-on-light)' : 'var(--text)', shape: 'diamond' }],
    [locSeries, light],
  );

  const theme = light
    ? { grid: 'var(--chart-grid-light)', axis: 'var(--text-muted-on-light)', surface: 'var(--surface-light)' }
    : { grid: 'var(--border)', axis: 'var(--text-muted-alt)', surface: 'var(--surface)' };

  const rangeTotal = days.reduce((sum, d) => sum + d.total, 0);
  const hasData = days.some((d) => d.total > 0);

  return (
    <div className={`${s.panel} ${light ? s.light : ''}`}>
      <div className={s.toolbar}>
        <div>
          <div className={s.eyebrow}>Revenue across the shops</div>
          <h1 className={s.title}>Analytics</h1>
          <div className={s.subtitle}>
            {longDate(start)} – {longDate(end)}
            {hasData && <> · <span className={s.strong}>{money(rangeTotal)}</span> total</>}
          </div>
        </div>
        <div className={s.rangeControls}>
          <div className={s.rangeBtns} role="group" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.id}
                className={`${s.rangeBtn} ${!custom && rangeId === r.id ? s.rangeBtnActive : ''}`}
                onClick={() => selectPreset(r.id)}
              >
                {r.label}
              </button>
            ))}
            <button
              className={`${s.rangeBtn} ${custom ? s.rangeBtnActive : ''}`}
              onClick={enableCustom}
            >
              Custom
            </button>
          </div>
          {custom && (
            <div className={s.customRange}>
              <input
                type="date"
                className={s.dateInput}
                value={customStart}
                max={customEnd || today}
                onChange={(e) => setCustomStart(e.target.value)}
                aria-label="Start date"
              />
              <span className={s.dateDash}>–</span>
              <input
                type="date"
                className={s.dateInput}
                value={customEnd}
                min={customStart}
                max={today}
                onChange={(e) => setCustomEnd(e.target.value)}
                aria-label="End date"
              />
            </div>
          )}
        </div>
      </div>

      <div className={s.chartCard}>
        <div className={s.legend}>
          {series.map((serie) => (
            <span key={serie.id} className={s.legendItem}>
              <LegendSwatch shape={serie.shape} color={serie.color} />
              <span className={s.legendName}>{serie.name}</span>
            </span>
          ))}
        </div>

        {tooLong ? (
          <div className={s.state}>Please choose a range of 366 days or fewer.</div>
        ) : isLoading ? (
          <div className={s.state}>Loading…</div>
        ) : isError ? (
          <div className={s.state}>
            Failed to load analytics. <button className={s.retry} onClick={() => refetch()}>Retry</button>
          </div>
        ) : !hasData ? (
          <div className={s.state}>No revenue recorded in this range.</div>
        ) : (
          <>
            <RevenueChart days={days} series={series} theme={theme} />
            {isFetching && <div className={s.fetching}>Updating…</div>}
          </>
        )}
      </div>

      {!tooLong && !isLoading && !isError && days.length > 0 && <DataTable days={days} locSeries={locSeries} />}
    </div>
  );
}
